import { decodeJwt } from "@/utils/jwt";

/**
 * Tenant guard ("Pin the tenant per tab and send it explicitly").
 *
 * Problem: the JWT lives in `localStorage`, which is shared across all tabs of
 * the same origin. Logging into a different tenant in another tab overwrites the
 * token used by this tab, so a "save" here could silently write to the wrong
 * tenant.
 *
 * Fix: on first load, each tab "pins" the tenant it was opened for into
 * `sessionStorage` (which is isolated per tab). We then send that pinned tenant
 * as an explicit `x-tenant-name` header on every request. The backend validates
 * that the header matches the tenant encoded in the JWT; if they differ (because
 * the token was overwritten by another tab), it rejects the request and the tab
 * is locked until the user reloads.
 */

export const TENANT_HEADER = "x-tenant-name";

const SESSION_TENANT_KEY = "bmn.tabTenantName";
const OVERLAY_ID = "tenant-mismatch-overlay";

/**
 * Read the tenant name encoded in the currently stored access token.
 * @returns {string|null}
 */
export const getTenantFromToken = () => {
  const accessToken = localStorage.getItem("accessToken");
  if (!accessToken) {
    return null;
  }

  try {
    const decoded = decodeJwt(accessToken);
    return decoded?.tenantName ?? null;
  } catch {
    return null;
  }
};

/**
 * Pin the tenant for this tab. Should be called once, early in app bootstrap,
 * while the token in `localStorage` still belongs to this tab's tenant.
 *
 * This must run on every page load (including reloads) and (re-)syncs the pin to
 * the tenant of the token currently in `localStorage`. That is safe because a
 * tab's `sessionStorage` can only ever be written by that same tab — another tab
 * cannot touch it. Cross-tab tenant changes that happen while this tab is alive
 * are instead detected via the `storage` event and the backend 409 response, not
 * via this function.
 * @param {string|null} [tenantName]
 */
export const pinTabTenant = (tenantName = getTenantFromToken()) => {
  if (!tenantName) {
    return;
  }

  sessionStorage.setItem(SESSION_TENANT_KEY, tenantName);
};

/**
 * The tenant this tab is pinned to (falls back to the token tenant if, for some
 * reason, the tab was never pinned).
 * @returns {string|null}
 */
export const getTabTenant = () => {
  return sessionStorage.getItem(SESSION_TENANT_KEY) || getTenantFromToken();
};

/**
 * Clear the pinned tenant (e.g. on logout).
 */
export const clearTabTenant = () => {
  sessionStorage.removeItem(SESSION_TENANT_KEY);
};

/**
 * Whether the tab's pinned tenant still matches the tenant in the current token.
 * A `false` result means another tab logged into a different tenant.
 * @returns {boolean}
 */
export const isTabTenantStale = () => {
  const pinned = sessionStorage.getItem(SESSION_TENANT_KEY);
  const current = getTenantFromToken();
  return Boolean(pinned && current && pinned !== current);
};

/**
 * Lock the tab with a blocking overlay explaining that the session was replaced
 * by a login in another tab. The only action is to reload, which re-pins the tab
 * to the now-current tenant.
 */
export const lockTabForTenantMismatch = () => {
  if (typeof document === "undefined" || document.getElementById(OVERLAY_ID)) {
    return;
  }

  const overlay = document.createElement("div");
  overlay.id = OVERLAY_ID;
  overlay.setAttribute("role", "alertdialog");
  overlay.style.cssText = [
    "position:fixed",
    "inset:0",
    "z-index:2147483647",
    "display:flex",
    "align-items:center",
    "justify-content:center",
    "background:rgba(0,0,0,0.6)",
    "padding:16px",
  ].join(";");

  const card = document.createElement("div");
  card.style.cssText = [
    "max-width:420px",
    "width:100%",
    "background:#fff",
    "border-radius:12px",
    "box-shadow:0 10px 30px rgba(0,0,0,0.25)",
    "padding:24px",
    "text-align:center",
    "font-family:inherit",
  ].join(";");

  const title = document.createElement("h4");
  title.textContent = "Сесію змінено в іншій вкладці";
  title.style.cssText = "margin:0 0 12px;color:#344767;font-weight:700";

  const text = document.createElement("p");
  text.textContent =
    "Ви увійшли в іншу компанію в іншій вкладці браузера. " +
    "Перезавантажте сторінку, щоб продовжити.";
  text.style.cssText = "margin:0 0 20px;color:#67748e;font-size:14px";

  const button = document.createElement("button");
  button.type = "button";
  button.textContent = "Перезавантажити";
  button.style.cssText = [
    "background:#ffbb0d",
    "color:#000",
    "border:none",
    "border-radius:8px",
    "padding:10px 20px",
    "font-weight:700",
    "cursor:pointer",
  ].join(";");
  button.addEventListener("click", () => window.location.reload());

  card.appendChild(title);
  card.appendChild(text);
  card.appendChild(button);
  overlay.appendChild(card);
  document.body.appendChild(overlay);
};

/**
 * Start listening for token changes made by other tabs. As soon as another tab
 * logs into a different tenant, this tab is locked immediately (without waiting
 * for the next request).
 */
export const initTenantGuard = () => {
  if (typeof window === "undefined") {
    return;
  }

  pinTabTenant();

  window.addEventListener("storage", (event) => {
    if (event.key !== "accessToken") {
      return;
    }
    if (isTabTenantStale()) {
      lockTabForTenantMismatch();
    }
  });
};
